import React, { useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import ToggleSwitch from "@trendmicro/react-toggle-switch";
import { LuClock, LuCircleHelp, LuArrowRightLeft, LuInfo, LuTriangleAlert } from "react-icons/lu";
import { QUESTION_MANDATORY_TYPES } from "../../../../common/sceneDefaults.es6";

// Empty means the scene waits for its own duration.
function DurationField({ id, value, onChange }) {
  return (
    <div className="gc-field gc-field--inline">
      <label htmlFor={id} className="gc-label">
        {i18n.t("slideDuration")}
      </label>
      <div className="gc-input-affix">
        <input
          id={id}
          type="number"
          min={0}
          value={value > 0 ? value : ""}
          placeholder={i18n.t("global_config.scene_duration_placeholder")}
          className="gc-input"
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        />
        <span className="gc-input-affix__unit">s</span>
      </div>
    </div>
  );
}

DurationField.propTypes = {
  id: PropTypes.string,
  value: PropTypes.number,
  onChange: PropTypes.func,
};

// Completion time of scenes without a mandatory rule.
function DefaultDurationField({ value, fallbackSec, onChange }) {
  return (
    <>
      <div className="gc-field gc-field--inline">
        <label htmlFor="default-scene-duration" className="gc-label">
          {i18n.t("global_config.default_scene_duration")}
        </label>
        <div className="gc-input-affix">
          <input
            id="default-scene-duration"
            type="number"
            min={0}
            value={value > 0 ? value : ""}
            placeholder={i18n.t("global_config.default_scene_duration_placeholder") || "auto"}
            className="gc-input"
            onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          />
          <span className="gc-input-affix__unit">s</span>
        </div>
      </div>
      <p className="gc-hint">
        {i18n.t("global_config.default_scene_duration_hint", { seconds: fallbackSec })}
      </p>
    </>
  );
}

DefaultDurationField.propTypes = {
  value: PropTypes.number,
  fallbackSec: PropTypes.number,
  onChange: PropTypes.func,
};

function ToggleRow({ checked, onChange, label, note }) {
  return (
    <label className="gc-toggle-row">
      <ToggleSwitch checked={checked} onChange={onChange} />
      <span>{label}</span>
      {note && (
        <span className="gc-info" title={note}>
          <LuInfo />
        </span>
      )}
    </label>
  );
}

ToggleRow.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  label: PropTypes.string,
  note: PropTypes.string,
};

// Separate tabs keep question rules off standard scenes.
const TABS = [
  { id: "standard", icon: LuClock, isOn: (m) => !!m.isMandatory },
  { id: "question", icon: LuCircleHelp, isOn: (m) => !!m.questionIsMandatory },
  { id: "transition", icon: LuArrowRightLeft, isOn: (m) => !!(m.autoNext || m.sceneEndCta) },
];

export default function SceneDefaultsSection({ duration, minTimeProgress, mandatory, onChange }) {
  // Same floor the Visor falls back to.
  const fallbackSec = minTimeProgress > 0 ? minTimeProgress : 3;
  const updateMandatory = (patch) => {
    onChange({ mandatory: { ...mandatory, ...patch } });
  };
  const [tab, setTab] = useState("standard");
  const waitsForAnswer = QUESTION_MANDATORY_TYPES.includes(mandatory.questionMandatoryType);

  return (
    <div className="gc-stack">
      <p className="gc-warning" role="note">
        <LuTriangleAlert className="gc-warning__icon" />
        <span>{i18n.t("global_config.scene_defaults_override_warning")}</span>
      </p>

      <div className="gc-tabs" role="tablist">
        {TABS.map(({ id, icon: Icon, isOn }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={"gc-tab" + (tab === id ? " is-active" : "")}
            onClick={() => setTab(id)}
          >
            <Icon className="gc-tab__icon" />
            <span className="gc-tab__label">{i18n.t(`global_config.scene_group.${id}`)}</span>
            {isOn(mandatory) && <span className="gc-tab__dot" />}
          </button>
        ))}
      </div>

      <div className="gc-tabpanel" role="tabpanel">
        <p className="gc-hint">{i18n.t(`global_config.scene_group.${tab}_hint`)}</p>

        {tab === "standard" && (
          <>
            <ToggleRow
              checked={!!mandatory.isMandatory}
              // Each switch starts the duration clean.
              onChange={() => updateMandatory({ isMandatory: !mandatory.isMandatory, minTime: 0 })}
              label={i18n.t("global_config.default_mandatory_label")}
            />
            {mandatory.isMandatory ? (
              <div className="gc-group__sub">
                <DurationField
                  id="default-min-time"
                  value={mandatory.minTime}
                  onChange={(minTime) => updateMandatory({ minTime })}
                />
                <p className="gc-hint">{i18n.t("global_config.wait_for_time_hint")}</p>
              </div>
            ) : (
              <DefaultDurationField
                value={duration}
                fallbackSec={fallbackSec}
                onChange={(next) => onChange({ duration: next })}
              />
            )}
          </>
        )}

        {tab === "question" && (
          <>
            <ToggleRow
              checked={!!mandatory.questionIsMandatory}
              onChange={() =>
                updateMandatory({
                  questionIsMandatory: !mandatory.questionIsMandatory,
                  questionMinTime: 0,
                })
              }
              label={i18n.t("global_config.default_mandatory_label")}
            />
            {!mandatory.questionIsMandatory && (
              <DefaultDurationField
                value={mandatory.questionDefaultDuration}
                fallbackSec={fallbackSec}
                onChange={(questionDefaultDuration) => updateMandatory({ questionDefaultDuration })}
              />
            )}
            {mandatory.questionIsMandatory && (
              <div className="gc-group__sub">
                <div className="gc-radio-group">
                  {["waitForTime", "waitForAction"].map((t) => (
                    <label key={t} className="gc-radio">
                      <input
                        type="radio"
                        name="default-question-mandatory-type"
                        value={t}
                        checked={waitsForAnswer ? t === "waitForAction" : t === "waitForTime"}
                        onChange={() => updateMandatory({ questionMandatoryType: t })}
                      />
                      <span>{i18n.t(t)}</span>
                    </label>
                  ))}
                </div>
                <p className="gc-hint">{i18n.t("global_config.wait_for_time_hint")}</p>

                {/* Same as the scene toolbar: a switch on top of "wait for question". */}
                {waitsForAnswer ? (
                  <ToggleRow
                    checked={mandatory.questionMandatoryType === "waitForCorrectAction"}
                    onChange={() =>
                      updateMandatory({
                        questionMandatoryType:
                          mandatory.questionMandatoryType === "waitForCorrectAction"
                            ? "waitForAction"
                            : "waitForCorrectAction",
                      })
                    }
                    label={i18n.t("waitForCorrectAction")}
                  />
                ) : (
                  <DurationField
                    id="default-question-min-time"
                    value={mandatory.questionMinTime}
                    onChange={(questionMinTime) => updateMandatory({ questionMinTime })}
                  />
                )}
              </div>
            )}
          </>
        )}

        {tab === "transition" && (
          <>
            {/* Independent of the mandatory setting. */}
            <ToggleRow
              checked={!!mandatory.autoNext}
              onChange={() =>
                updateMandatory(
                  // Turning auto-advance off also drops the countdown card opt-in.
                  mandatory.autoNext
                    ? { autoNext: false, autoNextShowCta: false }
                    : { autoNext: true }
                )
              }
              label={i18n.t("global_config.auto_next")}
              note={i18n.t("autoNextNote")}
            />
            {/* The card follows autoNextShowCta while auto-advancing, sceneEndCta otherwise. */}
            {mandatory.autoNext ? (
              <ToggleRow
                checked={!!mandatory.autoNextShowCta}
                onChange={() => updateMandatory({ autoNextShowCta: !mandatory.autoNextShowCta })}
                label={i18n.t("autoNextShowCta")}
                note={i18n.t("autoNextShowCtaNote")}
              />
            ) : (
              <ToggleRow
                checked={!!mandatory.sceneEndCta}
                onChange={() => updateMandatory({ sceneEndCta: !mandatory.sceneEndCta })}
                label={i18n.t("sceneEndCta")}
                note={i18n.t("sceneEndCtaNote")}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

SceneDefaultsSection.propTypes = {
  duration: PropTypes.number.isRequired,
  minTimeProgress: PropTypes.number,
  mandatory: PropTypes.shape({
    isMandatory: PropTypes.bool,
    minTime: PropTypes.number,
    questionIsMandatory: PropTypes.bool,
    questionMandatoryType: PropTypes.string,
    questionMinTime: PropTypes.number,
    questionDefaultDuration: PropTypes.number,
    autoNext: PropTypes.bool,
    autoNextShowCta: PropTypes.bool,
    sceneEndCta: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};
